import type { ChangeLogActionFilter, ChangeLogTimeRange, InventoryChangeEntry, PoolChangeLogModel } from '.';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { InventoryChangeKind } from '@thinkalpha/locates-ws-client';
import type { ChangeLogEntry as ApiChangeLogEntry, GetPoolChangeLogResponse } from '@thinkalpha/locates-ws-client';
import type { FirmMembershipWithProfile } from '@thinkalpha/ui-types/types/admin/firms';
import type { DetailedUser } from '@thinkalpha/ui-types/types/admin/users';
import { t } from 'i18next';
import { log } from 'src/admin/LAB/log';
import { sanitizeFilenameSegment, triggerCsvDownload } from 'src/admin/LAB/PoolManagement/csvDownload';
import { createChangeLogCsv } from 'src/admin/LAB/PoolManagement/poolChangeLogCsv';
import type { InventoryRowsPerPage } from 'src/admin/models/PoolInventoryModel';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getFirmMembersQuery } from 'src/queries/firms';
import { getAllPoolChangeLogQuery, getPoolChangeLogQuery } from 'src/queries/labPools';
import { getUsersQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Action filter options offered for a modern pool: every change kind the
 * modern journal enumerates. Legacy pools only ever record quantity
 * movements — the backend maps their journal onto the modern enumeration
 * in the single `increment` case — so an action filter carries no real
 * signal there: they get just `'all'`, and the view hides the dropdown
 * when there is nothing to choose.
 */
const MODERN_ACTION_FILTER_VALUES: readonly ChangeLogActionFilter[] = ['all', ...Object.values(InventoryChangeKind)];
const LEGACY_ACTION_FILTER_VALUES: readonly ChangeLogActionFilter[] = ['all'];

function timeRangeCutoffMs(range: ChangeLogTimeRange): number | null {
    if (range === 'custom') return null;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (range === 'today') return start.getTime();
    const days = range === 'last-7-days' ? 7 : 30;
    // Subtract `days - 1` so "last 7 days" covers today plus the previous
    // six calendar days, matching typical user mental model.
    start.setDate(start.getDate() - (days - 1));
    return start.getTime();
}

/**
 * Build the OData `$filter` sent to the change-log endpoint from the active
 * symbol / action / time-range filters, joined with `and`, matching the
 * clause grammar the backend's `validateChangeLogFilter` reduces to journal
 * params (it ignores the whole expression, with a warning, on anything else):
 *   - time:   `timestamp ge '<local-start-of-day ISO>'` (date literal must be quoted)
 *   - action: `kind eq '<InventoryChangeKind>'` (exact change kind)
 *   - symbol: `symbol eq '<UPPER>'` (exact match; `contains()` is rejected)
 * The action filter is passed in post-coercion (legacy pools only support a
 * subset of kinds). Returns `undefined` when nothing is active. Filtering is
 * delegated to the backend so it spans the whole log and stays consistent
 * with pagination.
 */
function buildChangeLogFilter(
    timeRange: ChangeLogTimeRange,
    actionFilter: ChangeLogActionFilter,
    symbolFilter: string,
): string | undefined {
    const clauses: string[] = [];
    const cutoff = timeRangeCutoffMs(timeRange);
    if (cutoff !== null) clauses.push(`timestamp ge '${new Date(cutoff).toISOString()}'`);
    if (actionFilter !== 'all') clauses.push(`kind eq '${actionFilter}'`);
    const symbol = symbolFilter.trim();
    // Escape single quotes per OData string-literal rules (doubled).
    if (symbol) clauses.push(`symbol eq '${symbol.toUpperCase().replace(/'/g, "''")}'`);
    return clauses.length ? clauses.join(' and ') : undefined;
}

/** `userId -> display name`; see {@link InventoryChangeEntry.userName}. */
export type ChangeLogUserNames = ReadonlyMap<string, string>;

/** Full name, else email; `null` when the profile is missing or has neither, so the id shows instead. */
function displayName(profile: { firstName: string; lastName: string; email: string } | undefined): string | null {
    if (!profile) return null;
    return `${profile.firstName} ${profile.lastName}`.trim() || profile.email.trim() || null;
}

function userNamesFrom<T>(
    items: ReadonlyArray<T> | undefined,
    entry: (item: T) => [string, string | null],
): ChangeLogUserNames {
    const map = new Map<string, string>();
    for (const item of items ?? []) {
        const [id, name] = entry(item);
        if (name !== null) map.set(id, name);
    }
    return map;
}

function userNamesFromUsers(users: ReadonlyArray<DetailedUser> | undefined): ChangeLogUserNames {
    return userNamesFrom(users, (user) => [user.id, displayName(user)]);
}

function userNamesFromFirmMembers(members: ReadonlyArray<FirmMembershipWithProfile> | undefined): ChangeLogUserNames {
    // The backend builds `profile` with a non-null assertion, so guard it despite the contract.
    return userNamesFrom(members, (member) => [member.userId, displayName(member.profile)]);
}

function authorIds(entries: ReadonlyArray<ApiChangeLogEntry>): string[] {
    return entries
        .map((entry) => entry.userId)
        .filter((id): id is string => id !== null)
        .values()
        .unique()
        .toArray();
}

function fromApiChangeLogEntry(entry: ApiChangeLogEntry, userNames: ChangeLogUserNames): InventoryChangeEntry {
    const ms = Date.parse(entry.timestamp);
    const userId = entry.userId;
    return {
        id: entry.id,
        timestamp: Number.isFinite(ms) ? ms : 0,
        userId,
        userName: userId === null ? 'System' : (userNames.get(userId) ?? userId),
        orderId: entry.orderId,
        kind: entry.kind,
        symbol: entry.symbol,
        quantity: entry.quantity,
        price: entry.price,
        available: entry.available,
    };
}

/** Everything the change-log model needs from its owner (the pool side). */
export interface PoolChangeLogModelDeps {
    /** The pool whose audit trail this is; part of the instance's identity. */
    readonly poolId: string;
    readonly queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly queryClient: ReactBindings['QueryClient'];
    /**
     * Query gate: fetch only while the Change Log tab is actually
     * showing — the audit log is large enough that it shouldn't refresh
     * eagerly alongside the inventory grid.
     */
    readonly isActive: ReadonlySignal<boolean>;
    /** Legacy pools offer no action filter (their journal has one kind). */
    readonly isLegacyPool: ReadonlySignal<boolean>;
    /** The pool's owning firm; `null` until the pool loads. Resolves author names for non-superusers. */
    readonly firmId: ReadonlySignal<string | null>;
    /**
     * Picks the author-name directory: superusers may bulk-look-up any
     * user id, while everyone else is limited to the pool firm's members
     * (the bulk endpoint 403s on users outside the caller's owned firms).
     */
    readonly hasSuperuserSession: ReadonlySignal<boolean>;
    /** Pool display name for the CSV filename; falls back to `pool`. */
    readonly poolName: ReadonlySignal<string | null>;
    /**
     * Page-size preference. Owned by the parent (and shared across pool
     * switches — page size deliberately sticks while the page itself
     * resets with each instance); this model reads and writes it.
     */
    readonly rowsPerPage: Signal<InventoryRowsPerPage>;
    /** Toast sink for the CSV export's failure path. */
    readonly store: ReactBindings['Store'];
}

/** See {@link PoolChangeLogModel}. */
export class PoolChangeLogModelImpl implements PoolChangeLogModel {
    readonly #disposableStack = new DisposableStack();
    readonly #deps: PoolChangeLogModelDeps;
    readonly #query: QueryObserverModel<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>;
    readonly #usersQuery: QueryObserverModel<DetailedUser[], Error, DetailedUser[]>;
    readonly #firmMembersQuery: QueryObserverModel<
        ReadonlyArray<FirmMembershipWithProfile>,
        Error,
        ReadonlyArray<FirmMembershipWithProfile>
    >;

    readonly poolId: string;

    readonly #symbolFilter = signal('');
    readonly #storedActionFilter = signal<ChangeLogActionFilter>('all');
    readonly #timeRange = signal<ChangeLogTimeRange>('today');
    /** Raw user-driven page; reads clamp via {@link page}. */
    readonly #page = signal(1);

    constructor(deps: PoolChangeLogModelDeps) {
        this.#deps = deps;
        this.poolId = deps.poolId;

        const options = computed<QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>>(
            () => {
                if (!deps.isActive.value) {
                    return getPoolChangeLogQuery(undefined);
                }
                const perPage = deps.rowsPerPage.value;
                const filter = buildChangeLogFilter(
                    this.#timeRange.value,
                    this.actionFilter.value,
                    this.#symbolFilter.value,
                );
                // "all" pages the whole filtered log via real backend
                // pagination; virtualization keeps the DOM flat. Otherwise
                // fetch a single page via skip/limit. The raw (unclamped)
                // page is read here so reading the clamped value — which
                // derives from this query's `count` — can't form a cycle; a
                // separate effect snaps the stored page back into range once
                // `count` is known.
                if (perPage === 'all') {
                    return getAllPoolChangeLogQuery(deps.poolId, { filter });
                }
                const requested = this.#page.value;
                const page = Number.isFinite(requested) ? Math.max(1, Math.trunc(requested)) : 1;
                return getPoolChangeLogQuery(deps.poolId, { skip: (page - 1) * perPage, limit: perPage, filter });
            },
        );
        this.#query = deps.queryObserverModelFactory<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse>(
            options,
        );
        this.#disposableStack.use(this.#query);

        // Author directories, gated like the log itself. Only one of the
        // two is ever enabled, keyed on the session's superuser status.
        const usersOptions = computed<QueryObserverOptions<DetailedUser[], Error, DetailedUser[]>>(() => {
            const active = deps.isActive.value && deps.hasSuperuserSession.value;
            return getUsersQuery(active ? authorIds(this.#query.signal.value.data?.entries ?? []) : []);
        });
        this.#usersQuery = deps.queryObserverModelFactory<DetailedUser[], Error, DetailedUser[]>(usersOptions);
        this.#disposableStack.use(this.#usersQuery);

        const firmMembersOptions = computed<
            QueryObserverOptions<
                ReadonlyArray<FirmMembershipWithProfile>,
                Error,
                ReadonlyArray<FirmMembershipWithProfile>
            >
        >(() => {
            const active = deps.isActive.value && !deps.hasSuperuserSession.value;
            return getFirmMembersQuery(active ? (deps.firmId.value ?? undefined) : undefined);
        });
        this.#firmMembersQuery = deps.queryObserverModelFactory<
            ReadonlyArray<FirmMembershipWithProfile>,
            Error,
            ReadonlyArray<FirmMembershipWithProfile>
        >(firmMembersOptions);
        this.#disposableStack.use(this.#firmMembersQuery);

        // Once the backend reports `count`, snap an out-of-range stored page
        // back into range so the fetched slice matches the displayed
        // (clamped) page instead of requesting an empty skip. Writing the
        // clamped page is idempotent (re-running sees `page === max` and
        // stops).
        this.#disposableStack.defer(
            effect(() => {
                if (!deps.isActive.value || deps.rowsPerPage.value === 'all') return;
                // Only clamp once a real count is in. While the new page is
                // still loading the query exposes `data === undefined`;
                // treating that as 0 would snap back to page 1 and block
                // paging forward.
                const count = this.#query.signal.value.data?.count;
                if (count === undefined) return;
                const max = Math.max(1, Math.ceil(count / deps.rowsPerPage.value));
                if (this.#page.value > max) this.#page.value = max;
            }),
        );
    }

    readonly isLoading: ReadonlySignal<boolean> = computed(() => {
        const q = this.#query.signal.value;
        return q.data === undefined && q.isLoading === true;
    });

    /**
     * Author display names for the fetched page. Entries render before
     * the directory answers (the id shows until then), so a slow or
     * failed lookup never blocks the log.
     */
    readonly #userNames: ReadonlySignal<ChangeLogUserNames> = computed(() =>
        this.#deps.hasSuperuserSession.value
            ? userNamesFromUsers(this.#usersQuery.signal.value.data)
            : userNamesFromFirmMembers(this.#firmMembersQuery.signal.value.data),
    );

    readonly entries: ReadonlySignal<ReadonlyArray<InventoryChangeEntry>> = computed(() => {
        const data = this.#query.signal.value.data;
        if (!data) return [];
        const userNames = this.#userNames.value;
        // Newest-first; backend ordering isn't part of the contract so we
        // sort here to keep the timeline / day grouping stable.
        return data.entries
            .map((entry) => fromApiChangeLogEntry(entry, userNames))
            .sort((a, b) => b.timestamp - a.timestamp);
    });

    readonly symbolFilter: ReadonlySignal<string> = computed(() => this.#symbolFilter.value);
    readonly actionFilterValues: ReadonlySignal<readonly ChangeLogActionFilter[]> = computed(() =>
        this.#deps.isLegacyPool.value ? LEGACY_ACTION_FILTER_VALUES : MODERN_ACTION_FILTER_VALUES,
    );
    readonly actionFilter: ReadonlySignal<ChangeLogActionFilter> = computed(() => {
        const stored = this.#storedActionFilter.value;
        return this.actionFilterValues.value.includes(stored) ? stored : 'all';
    });
    readonly timeRange: ReadonlySignal<ChangeLogTimeRange> = computed(() => this.#timeRange.value);

    // Every filter change snaps back to page 1: the filtered log is a new
    // result set and a stale skip could point past its end.
    setSymbolFilter(query: string): void {
        if (this.#symbolFilter.value === query) return;
        this.#symbolFilter.value = query;
        this.#page.value = 1;
    }
    setActionFilter(filter: ChangeLogActionFilter): void {
        if (this.#storedActionFilter.value === filter) return;
        this.#storedActionFilter.value = filter;
        this.#page.value = 1;
    }
    setTimeRange(range: ChangeLogTimeRange): void {
        if (this.#timeRange.value === range) return;
        this.#timeRange.value = range;
        this.#page.value = 1;
    }

    readonly totalCount: ReadonlySignal<number> = computed(() => this.#query.signal.value.data?.count ?? 0);
    readonly rowsPerPage: ReadonlySignal<InventoryRowsPerPage> = computed(() => this.#deps.rowsPerPage.value);
    readonly pageCount: ReadonlySignal<number> = computed(() => {
        const perPage = this.#deps.rowsPerPage.value;
        if (perPage === 'all') return 1;
        return Math.max(1, Math.ceil(this.totalCount.value / perPage));
    });
    readonly page: ReadonlySignal<number> = computed(() => {
        const requested = this.#page.value;
        const max = this.pageCount.value;
        if (!Number.isFinite(requested) || requested < 1) return 1;
        if (requested > max) return max;
        return Math.floor(requested);
    });

    setPage(page: number): void {
        this.#page.value = page;
    }
    setRowsPerPage(rowsPerPage: InventoryRowsPerPage): void {
        if (this.#deps.rowsPerPage.value === rowsPerPage) return;
        this.#deps.rowsPerPage.value = rowsPerPage;
        // A new page size re-slices the log; snap to the first page so the
        // request can't point past the new page count.
        this.#page.value = 1;
    }

    /** True while {@link downloadCsv} is fetching and building the export. */
    readonly #isDownloadingCsv = signal(false);
    readonly isDownloadingCsv: ReadonlySignal<boolean> = computed(() => this.#isDownloadingCsv.value);

    async downloadCsv(): Promise<void> {
        if (typeof document === 'undefined') return;
        // Guard re-entrancy: the export button disables on this flag, but a
        // keyboard-repeat or programmatic caller could still fire twice.
        if (this.#isDownloadingCsv.value) return;
        this.#isDownloadingCsv.value = true;
        try {
            await this.#downloadCsvInternal();
        } catch (error) {
            // Surface a failed all-rows fetch instead of leaking an
            // unhandled rejection from the fire-and-forget caller.
            const message = error instanceof Error ? error.message : String(error);
            this.#deps.store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-csv-export-failed', { message }),
                    group: 'default',
                    variant: 'error',
                },
            });
        } finally {
            this.#isDownloadingCsv.value = false;
        }
    }

    async #downloadCsvInternal(): Promise<void> {
        // Export the entire filtered audit trail, not just the current page.
        // Gather it via the all-rows query with the active filter (same cache
        // key as the tab's "all" mode, so it's often already warm); the
        // timeline's day grouping isn't carried into the CSV.
        const filter = buildChangeLogFilter(this.#timeRange.value, this.actionFilter.value, this.#symbolFilter.value);
        const allOptions = getAllPoolChangeLogQuery(this.poolId, { filter });
        const response = await this.#deps.queryClient.fetchQuery({
            ...allOptions,
            queryKey: allOptions.queryKey ?? [],
        });
        if (response.entries.length === 0) return;
        const userNames = await this.#fetchUserNames(authorIds(response.entries));
        const entries = response.entries
            .map((entry) => fromApiChangeLogEntry(entry, userNames))
            .sort((a, b) => b.timestamp - a.timestamp);
        const csv = createChangeLogCsv(entries, {
            columnLabels: {
                timestamp: t('admin::pool-management::changelog-export-col-timestamp'),
                user: t('admin::pool-management::changelog-export-col-user'),
                symbol: t('admin::pool-management::changelog-export-col-symbol'),
                action: t('admin::pool-management::changelog-export-col-action'),
                quantity: t('admin::pool-management::changelog-export-col-quantity'),
                price: t('admin::pool-management::changelog-export-col-price'),
                orderId: t('admin::pool-management::changelog-export-col-order-id'),
            },
        });
        const safeName = sanitizeFilenameSegment(this.#deps.poolName.value ?? '') || 'pool';
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        triggerCsvDownload(csv, `${safeName}_change-log_${yyyy}-${mm}-${dd}.csv`);
    }

    /**
     * Author names for the export, which spans every page and so can
     * reach ids the tab's page-scoped directory hasn't seen. Same cache
     * keys as the observers, so the current page's lookup is reused.
     * Best-effort: a failed lookup leaves the ids in the file.
     */
    async #fetchUserNames(userIds: readonly string[]): Promise<ChangeLogUserNames> {
        if (userIds.length === 0) return new Map();
        try {
            if (this.#deps.hasSuperuserSession.value) {
                const options = getUsersQuery(userIds);
                return userNamesFromUsers(
                    await this.#deps.queryClient.fetchQuery({ ...options, queryKey: options.queryKey ?? [] }),
                );
            }
            const firmId = this.#deps.firmId.value;
            if (firmId === null) return new Map();
            // `fetchQuery` rejects the observer-only `enabled` flag.
            const { queryKey, queryFn } = getFirmMembersQuery(firmId);
            const members = await this.#deps.queryClient.fetchQuery<ReadonlyArray<FirmMembershipWithProfile>>({
                queryKey,
                queryFn,
            });
            return userNamesFromFirmMembers(members);
        } catch (error) {
            log.warn(`Author lookup for pool ${this.poolId} change-log export failed; exporting ids instead`, {
                error,
                poolId: this.poolId,
            });
            return new Map();
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
